// The list of every job (Session SC7A). One page, two tabs of the calm menu:
//   * All jobs (Schedule hub, /office/jobs): every job of every type in one list, filterable by type, status (on hold for parts
//     included), engineer, customer and date, so a person who does not know which menu a job lives under finds it in one place.
//   * Repair jobs (Repairs hub, /office/repair-jobs): the same list fixed to repair and technician visit jobs that are still open.
// READ ONLY. A row opens the job's own record (its call-out, its project, or its visit record); nothing here changes a job.
//
// The list is server-paged 50 at a time (GET /api/scheduling/jobs, the shared paging contract): the platform has about 14,000 jobs a
// year at 1,000 lifts, so nothing here ever loads them all. Job types are named and coloured only through src/lib/jobType.ts.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import type { JobListItem, ScheduleEngineer } from "../../lib/api";
import { Pager } from "../../components/Pager";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { JobStatePill } from "../../components/StatusPill";
import { followOnWorksHref, isJobOverdue } from "../../lib/statusRegistry";
import { JOB_HOLD_REASON_OPTIONS, type JobHoldReasonKey } from "../../lib/jobHold";
import JobTypeBadge from "../../components/JobTypeBadge";
import { RecordPicker } from "../../components/RecordPicker";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { AccountSymbol } from "../../components/AccountSymbol";
import { SortableHeader } from "../../components/SortableHeader";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { useListSearch } from "../../lib/useListSearch";
import { useServerPage } from "../../lib/useServerPage";
import { useSortableColumn } from "../../lib/useSortableColumn";
import { formatDate, londonDateKey } from "../../lib/format";

import { JOB_TYPE_KEYS, jobTypeLabel } from "../../lib/jobType";

const PAGE_SIZE = 50;

// The types the All jobs filter offers: every job type, named and coloured only through src/lib/jobType.ts.
const TYPE_OPTIONS = JOB_TYPE_KEYS;

// Status filter words. "On hold" is the screen's name for a job on hold (status parts_pending), whatever the reason (Session SC9A).
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "open", label: "Open (not finished)" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In progress" },
  { value: "on_hold", label: "On hold" },
  { value: "pending_approval", label: "Closed, awaiting release" },
  { value: "complete", label: "Complete" },
  { value: "cancelled", label: "Cancelled" },
];

const REPAIR_TYPES = ["repair", "tech_visit"];

/** Where a job's row opens: its call-out, its project, or its visit record. Pure. */
export function jobRecordHref(j: Pick<JobListItem, "id" | "calloutId" | "projectId" | "type" | "hasSheet" | "scheduledFor">): string {
  if (j.calloutId) return `/office/callouts/${encodeURIComponent(j.calloutId)}`;
  if ((j.type === "project_works" || j.type === "project_survey") && j.projectId) return `/office/projects/${encodeURIComponent(j.projectId)}`;
  // A visit record exists only once an engineer has opened or sent the sheet; before that /office/jobsheets/:id has nothing to show.
  if (j.hasSheet) return `/office/jobsheets/${encodeURIComponent(j.id)}`;
  if (j.type === "loler_follow_up") return "/office/loler?tab=to-close";
  if (j.type === "safed_test") return "/office/safed";
  // Everything else with no record yet opens in the calendar, on its day, with its own detail popover open (Scheduling ?date= and ?job=).
  const day = j.scheduledFor ? londonDateKey(j.scheduledFor) : null;
  return `/office/scheduling?${day ? `date=${day}&` : ""}job=${encodeURIComponent(j.id)}`;
}

const SELECT =
  "rounded-ele border border-ele-purple-accent bg-ele-field-inset px-2 py-1.5 text-xs text-ele-strong max-sm:min-h-11";

export function JobsListPage({ mode }: { mode: "all" | "repairs" }) {
  const repairs = mode === "repairs";
  const [mobileCards] = useState(initialMobileLayout);
  const [params, setParams] = useSearchParams();
  const { search, setSearch } = useListSearch();
  const q = (params.get("q") ?? "").trim();
  const sort = useSortableColumn(repairs ? "repair-jobs" : "all-jobs");

  const type = repairs ? "" : (params.get("type") ?? "");
  // Both tabs open on the work still to do, soonest first (overdue, then upcoming), so page one is what needs doing; "Every status" widens it.
  const status = params.get("status") ?? "open";
  // Session SC9A: narrow the held jobs to one reason. Only meaningful with the On hold status.
  const holdReason = (params.get("holdReason") ?? "") as JobHoldReasonKey | "";
  const engineerId = params.get("engineerId") ?? "";
  const customerId = params.get("customerId") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";

  const [engineers, setEngineers] = useState<ScheduleEngineer[]>([]);
  useEffect(() => {
    let live = true;
    api.scheduling
      .engineers()
      .then((e) => live && setEngineers(e))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  function setFilter(key: string, value: string) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  }

  const queryKey = JSON.stringify({ mode, type, status, holdReason, engineerId, customerId, from, to, q, sk: sort.sortKey, sd: sort.sortDir });
  const page = useServerPage(queryKey, (at) =>
    api.scheduling.jobList({
      types: repairs ? REPAIR_TYPES : type ? [type] : [],
      status: status && status !== "any" ? [status] : [],
      ...(status === "on_hold" && holdReason ? { holdReason: [holdReason] } : {}),
      ...(engineerId ? { engineerId } : {}),
      ...(customerId ? { customerId } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(q ? { q } : {}),
      ...(sort.sortKey ? { sort: sort.sortKey as "date" | "type" | "status" | "site" | "engineer", dir: sort.sortDir } : {}),
      limit: PAGE_SIZE,
      offset: at,
    }),
  );
  const rows = page.data?.items ?? [];
  const today = londonDateKey(new Date());
  /** The row's ONE state pill (statusRegistry.ts): entrapment, overdue, on hold, SLA held, follow-on works, then the ordinary status. */
  const rowState = (j: JobListItem, className?: string, linked = true) => (
    <JobStatePill
      className={className}
      // On a phone card the whole card is already a link, so the pill must not hold a second one (a link inside a link is invalid HTML).
      jobId={linked ? j.id : null}
      facts={{
        status: j.status,
        entrapment: j.entrapment,
        overdue: isJobOverdue(j.status, j.scheduledFor ? londonDateKey(j.scheduledFor) : null, today),
        holdReason: j.holdReason,
        holdReasonText: j.holdReasonText,
        slaHeldLabel: j.slaHeldLabel,
        followOnWorks: j.followOnWorks,
      }}
    />
  );
  const total = page.data?.total ?? 0;
  const narrowed = Boolean(q || engineerId || customerId || from || to || (!repairs && type) || status !== "open" || holdReason);

  function clearAll() {
    setSearch("");
    setParams(new URLSearchParams(), { replace: true });
  }

  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      {!repairs && (
        <select aria-label="Job type" value={type} onChange={(e) => setFilter("type", e.target.value)} className={SELECT}>
          <option value="">Every type</option>
          {TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {jobTypeLabel(t)}
            </option>
          ))}
        </select>
      )}
      <select aria-label="Job status" value={status} onChange={(e) => setFilter("status", e.target.value)} className={SELECT}>
        <option value="any">{repairs ? "Any status" : "Every status"}</option>
        {STATUS_OPTIONS.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
      {status === "on_hold" && (
        <select aria-label="Hold reason" value={holdReason} onChange={(e) => setFilter("holdReason", e.target.value)} className={SELECT}>
          <option value="">Any reason</option>
          {JOB_HOLD_REASON_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      <select aria-label="Engineer" value={engineerId} onChange={(e) => setFilter("engineerId", e.target.value)} className={SELECT}>
        <option value="">Any engineer</option>
        {engineers.map((e) => (
          <option key={e.id} value={e.id}>
            {e.name}
          </option>
        ))}
      </select>
      <label className="flex items-center gap-1 text-xs text-ele-body">
        From
        <input type="date" aria-label="From date" value={from} onChange={(e) => setFilter("from", e.target.value)} className={SELECT} />
      </label>
      <label className="flex items-center gap-1 text-xs text-ele-body">
        To
        <input type="date" aria-label="To date" value={to} onChange={(e) => setFilter("to", e.target.value)} className={SELECT} />
      </label>
      <div className="min-w-[12rem]">
        <RecordPicker kind="customer" id="jobs-customer" ariaLabel="Customer" value={customerId} placeholder="Any customer" onChange={(id) => setFilter("customerId", id)} />
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        className="mb-4"
        title={repairs ? "Repair jobs" : "All jobs"}
        subtitle={
          repairs
            ? "The repair and technician visit jobs still open: who is booked, when, and which are on hold. A job opens its own record."
            : "Every job of every type in one list. Filter by type, status, engineer, customer and date; a job opens its own record."
        }
      />
      <ListToolbar
        className="mb-3"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Lift, site, customer or engineer"
        searchLabel="Search jobs"
        filters={filters}
        resultCount={narrowed ? { shown: rows.length, total, noun: "jobs" } : undefined}
        onClear={narrowed ? clearAll : undefined}
      />

      {page.loading ? (
        <Loader />
      ) : page.error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the jobs. Please try again in a moment.
        </p>
      ) : rows.length === 0 ? (
        <EmptyState role={narrowed ? "status" : undefined} message={narrowed ? "No jobs match these filters." : repairs ? "No open repair jobs." : "No jobs yet."} />
      ) : mobileCards ? (
        <MobileCardList label={repairs ? "Repair jobs" : "All jobs"}>
          {rows.map((j) => (
            <MobileListCard
              key={j.id}
              to={jobRecordHref(j)}
              primary={
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-heading">{j.reference ?? j.eleNumber ?? "Job"}</span>
                  <JobTypeBadge type={j.type} size="sm" />
                </span>
              }
              secondary={
                <>
                  {/* Session SCR1 (SC9B follow-up): the account symbol, plain because the whole card is a link. */}
                  {j.customerId && <AccountSymbol plain customerId={j.customerId} className="!ml-0" />}
                  {[j.siteName, j.customerName].filter(Boolean).join(" | ")}
                </>
              }
              meta={[j.scheduledFor ? formatDate(j.scheduledFor) : "No date", j.engineerName ?? "No engineer"].join(" | ")}
              right={rowState(j, undefined, false)}
              actions={
                j.followOnWorks ? (
                  <Link to={followOnWorksHref(j.id)} className="text-sm text-ele-purple hover:underline">
                    See the faults to fix
                  </Link>
                ) : undefined
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "17%" }} />
              <col className="max-lg:hidden" style={{ width: "13%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "19%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Job</th>
                <SortableHeader label="Type" colKey="type" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Site" colKey="site" sort={sort} onToggle={sort.toggle} />
                <th className="max-lg:hidden px-3 py-2">Customer</th>
                <SortableHeader label="Engineer" colKey="engineer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Date" colKey="date" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
              </tr>
            </thead>
            <tbody>
              {rows.map((j) => (
                <tr key={j.id} className="border-t border-ele-purple-accent">
                  <td className="px-3 py-2 align-top">
                    <Link to={jobRecordHref(j)} className="font-heading text-ele-strong hover:text-ele-green hover:underline">
                      {j.reference ?? j.eleNumber ?? "Open the job"}
                    </Link>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <JobTypeBadge type={j.type} size="sm" />
                  </td>
                  <td className="px-3 py-2 align-top">
                    {/* Session FR4: the Customer column is hidden below 1024px and the phone cards stop at 767px, so from 768 to 1023 the row had
                        no account symbol. It sits BEFORE the site name there (lg:hidden), as the Customer column puts it before the name: a list
                        table cell clips what overflows it (index.css .ele-list-table), so a symbol placed after a long name would be cut off. */}
                    {j.customerId && <AccountSymbol customerId={j.customerId} className="!ml-0 lg:hidden" />}
                    {j.siteId ? (
                      <Link to={`/office/sites/${encodeURIComponent(j.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
                        {j.siteName ?? "Site"}
                      </Link>
                    ) : (
                      (j.siteName ?? "")
                    )}
                  </td>
                  <td className="max-lg:hidden px-3 py-2 align-top">
                    {j.customerId && <AccountSymbol customerId={j.customerId} className="!ml-0" />}
                    {j.customerName ?? ""}
                  </td>
                  <td className="px-3 py-2 align-top">{j.engineerName ?? <span className="text-ele-body opacity-60">None yet</span>}</td>
                  <td className="whitespace-nowrap px-3 py-2 align-top">{j.scheduledFor ? formatDate(j.scheduledFor) : <span className="text-ele-body opacity-60">No date</span>}</td>
                  <td className="px-3 py-2 align-top">
                    {rowState(j, "whitespace-nowrap")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!page.loading && !page.error && (
        <div className={page.refreshing ? "opacity-60" : undefined}>
          <Pager offset={page.offset} total={total} shown={rows.length} pageSize={PAGE_SIZE} onOffset={page.setOffset} noun={["job", "jobs"]} />
        </div>
      )}
    </div>
  );
}

export function AllJobsPage() {
  return <JobsListPage mode="all" />;
}

export function RepairJobsPage() {
  return <JobsListPage mode="repairs" />;
}
